import { useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { fmtDT } from "../../components/hs/hsUtils";
import { BellSlash, Clock, X } from "@phosphor-icons/react";

const CHOICES = [[1, "1 day"], [3, "3 days"], [7, "1 week"]];

/** Grey "snoozed until … — note" chip (list rows + detail). */
export function SnoozedChip({ m, size = "sm" }) {
  if (!m.snooze) return null;
  const cls = size === "sm" ? "px-1 py-px text-[10px] max-w-[220px]" : "px-1.5 py-0.5 text-[11px]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border border-border bg-muted text-muted-foreground font-medium truncate ${cls}`} title={`Snoozed by ${m.snooze.by} until ${fmtDT(m.snooze.until)} — ${m.snooze.note}`} data-testid={`inbox-msg-snoozed-${m.id}`}>
      <BellSlash size={10} weight="bold" className="shrink-0" /> snoozed until {fmtDT(m.snooze.until).split(" ")[0]} — <span className="italic truncate">{m.snooze.note}</span>
    </span>
  );
}

/** Snooze button (popover with 1 / 3 / 7 days + required note) for an overdue email, or the end-snooze control when snoozed. */
export default function NudgeSnooze({ m, onChanged }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const snooze = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/inbox/messages/${m.id}/snooze`, { days, note });
      toast.success(data.message); setOpen(false); setNote(""); onChanged && onChanged(data.item);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't snooze"); } finally { setBusy(false); }
  };
  const unsnooze = async () => {
    setBusy(true);
    try { const { data } = await API.delete(`/inbox/messages/${m.id}/snooze`); toast.success(data.message); onChanged && onChanged(data.item); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't remove"); } finally { setBusy(false); }
  };
  if (m.snooze) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="inbox-detail-snoozed">
        <SnoozedChip m={m} size="md" />
        <span className="text-muted-foreground">by {m.snooze.by} · {fmtDT(m.snooze.at)} — the team won't be nudged until then.</span>
        <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={busy} onClick={unsnooze} data-testid="inbox-unsnooze-btn"><X size={11} className="mr-1" /> End snooze</Button>
      </div>
    );
  }
  if (!m.reply_overdue) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-6 text-[11px] border-amber-400 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/40" data-testid="inbox-snooze-btn"><BellSlash size={12} className="mr-1" /> Snooze nudge</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 space-y-2" data-testid="inbox-snooze-popover">
        <p className="text-xs font-semibold flex items-center gap-1"><Clock size={12} /> Pause the reply nudge</p>
        <div className="flex gap-1">
          {CHOICES.map(([d, label]) => <button key={d} type="button" onClick={() => setDays(d)} className={`flex-1 rounded-md border px-2 py-1 text-xs font-medium ${days === d ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`} data-testid={`inbox-snooze-days-${d}`}>{label}</button>)}
        </div>
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why? e.g. waiting on parts quote (required)" className="text-xs" maxLength={300} data-testid="inbox-snooze-note" />
        <p className="text-[11px] text-muted-foreground">Nobody is pinged while it's snoozed. If there's still no reply when it runs out, the team gets a fresh alert with your note.</p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs" disabled={busy || note.trim().length < 3} onClick={snooze} data-testid="inbox-snooze-save">{busy ? "Snoozing…" : `Snooze ${CHOICES.find(([d]) => d === days)[1]}`}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
